/**
 * Certificate Authentication Service
 * Simple authentication using Google Vision API text extraction and pattern matching
 */

import { googleVisionOCR } from './googleVisionOCR';

/**
 * Analyze certificate authenticity based on extracted text patterns
 * @param {string} extractedText - Text extracted from certificate
 * @returns {Object} Authentication result
 */
const analyzeCertificateAuthenticity = (extractedText) => {
  if (!extractedText || extractedText.trim().length === 0) {
    return {
      isAuthentic: false,
      confidence: 0,
      reason: 'No text could be extracted from the certificate',
      details: []
    };
  }

  const text = extractedText.toLowerCase();
  const details = [];
  let authenticityScore = 0;
  const maxScore = 10;

  // Check for authentic certificate indicators
  const authenticityIndicators = [
    {
      pattern: /(certificate|certification|certified)/,
      score: 2,
      description: 'Contains certificate terminology'
    },
    {
      pattern: /(university|college|institute|academy|school)/,
      score: 2,
      description: 'Contains educational institution reference'
    },
    {
      pattern: /(bachelor|master|diploma|degree|phd|ph\.d)/,
      score: 2,
      description: 'Contains degree/qualification terminology'
    },
    {
      pattern: /(\d{4}|\d{1,2}\/\d{1,2}\/\d{4}|january|february|march|april|may|june|july|august|september|october|november|december)/,
      score: 1,
      description: 'Contains date information'
    },
    {
      pattern: /(successfully|completed|achieved|awarded|conferred)/,
      score: 1,
      description: 'Contains completion/achievement language'
    },
    {
      pattern: /([a-z0-9]{6,}|roll|registration|id|serial)/,
      score: 1,
      description: 'Contains identification numbers or codes'
    },
    {
      pattern: /(dean|director|principal|registrar|president|chairman)/,
      score: 1,
      description: 'Contains authority/signatory titles'
    }
  ];

  // Check for suspicious patterns (fake certificate indicators)
  const suspiciousIndicators = [
    {
      pattern: /(fake|fraud|sample|test|demo|template)/,
      score: -5,
      description: 'Contains suspicious fake/demo language'
    },
    {
      pattern: /(free|download|generator|maker|create)/,
      score: -3,
      description: 'Contains generator/creation language'
    },
    {
      pattern: /(photoshop|edited|modified)/,
      score: -3,
      description: 'Contains editing software references'
    }
  ];

  // Analyze authentic indicators
  authenticityIndicators.forEach(indicator => {
    if (indicator.pattern.test(text)) {
      authenticityScore += indicator.score;
      details.push({
        type: 'positive',
        description: indicator.description,
        score: indicator.score
      });
    }
  });

  // Analyze suspicious indicators
  suspiciousIndicators.forEach(indicator => {
    if (indicator.pattern.test(text)) {
      authenticityScore += indicator.score; // This will subtract from score
      details.push({
        type: 'negative',
        description: indicator.description,
        score: indicator.score
      });
    }
  });

  // Calculate confidence percentage
  const confidence = Math.max(0, Math.min(100, (authenticityScore / maxScore) * 100));
  const isAuthentic = confidence >= 50; // 50% threshold

  return {
    isAuthentic,
    confidence: Math.round(confidence),
    reason: isAuthentic 
      ? `Certificate appears authentic with ${Math.round(confidence)}% confidence`
      : `Certificate appears suspicious with only ${Math.round(confidence)}% authenticity confidence`,
    details,
    score: authenticityScore,
    maxScore
  };
};

/**
 * Authenticate certificate using Google Vision API
 * @param {File} file - Certificate image file
 * @returns {Promise<Object>} Authentication result
 */
export const authenticateCertificate = async (file) => {
  try {
    console.log('🔐 Starting certificate authentication with Google Vision...');
    
    // Extract data using Google Vision
    const data = await googleVisionOCR.extractCertificateData(file);
    
    // Construct text representation for analysis
    // We combine all extracted fields to check for keywords
    const extractedText = [
      data.institution,
      data.student_name,
      data.certificate_type,
      data.program,
      data.issue_date,
      data.marks,
      data.roll_number,
      data.verification_code
    ].filter(Boolean).join(' ');
    
    if (!extractedText) {
      return {
        success: false,
        error: 'Could not extract text from certificate',
        isAuthentic: false,
        confidence: 0
      };
    }

    // Analyze authenticity
    const analysis = analyzeCertificateAuthenticity(extractedText);
    
    console.log('✅ Authentication complete:', analysis);
    
    return {
      success: true,
      extractedText,
      ...analysis,
      timestamp: new Date().toISOString(),
      structuredData: data // Return structured data too
    };

  } catch (error) {
    console.error('❌ Authentication error:', error);
    return {
      success: false,
      error: error.message,
      isAuthentic: false,
      confidence: 0
    };
  }
};

/**
 * Get authentication status display
 * @param {Object} authResult - Authentication result
 * @returns {Object} Display information
 */
export const getAuthenticationDisplay = (authResult) => {
  if (!authResult || !authResult.success) {
    return {
      icon: '❌',
      status: 'Error',
      message: authResult?.error || 'Authentication failed',
      color: 'red',
      bgColor: 'red-50'
    };
  }

  if (authResult.isAuthentic) {
    return {
      icon: '✅',
      status: 'Authentic',
      message: `Certificate verified as authentic with ${authResult.confidence}% confidence`,
      color: 'green',
      bgColor: 'green-50'
    };
  } else {
    return {
      icon: '⚠️',
      status: 'Suspicious',
      message: `Certificate authenticity uncertain (${authResult.confidence}% confidence)`,
      color: 'yellow',
      bgColor: 'yellow-50'
    };
  }
};

export default authenticateCertificate;